'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

class TreeNode {
  constructor(key, value, color = 1 /* TreeNodeColor.RED */) {
    this._left = undefined;
    this._right = undefined;
    this._parent = undefined;
    this._key = key;
    this._value = value;
    this._color = color;
  }
  /**
   * @description Get the pre node.
   * @returns TreeNode about the pre node.
   */
  _pre() {
    let preNode = this;
    const isRootOrHeader = preNode._parent._parent === preNode;
    if (isRootOrHeader && preNode._color === 1 /* TreeNodeColor.RED */) {
      preNode = preNode._right;
    } else if (preNode._left) {
      preNode = preNode._left;
      while (preNode._right) {
        preNode = preNode._right;
      }
    } else {
      // Must be root and left is null
      if (isRootOrHeader) {
        return preNode._parent;
      }
      let pre = preNode._parent;
      while (pre._left === preNode) {
        preNode = pre;
        pre = preNode._parent;
      }
      preNode = pre;
    }
    return preNode;
  }
  /**
   * @description Get the next node.
   * @returns TreeNode about the next node.
   */
  _next() {
    let nextNode = this;
    if (nextNode._right) {
      nextNode = nextNode._right;
      while (nextNode._left) {
        nextNode = nextNode._left;
      }
      return nextNode;
    } else {
      let pre = nextNode._parent;
      while (pre._right === nextNode) {
        nextNode = pre;
        pre = nextNode._parent;
      }
      if (nextNode._right !== pre) {
        return pre;
      } else return nextNode;
    }
  }
  /**
   * @description Rotate left.
   * @returns TreeNode about moved to original position after rotation.
   */
  _rotateLeft() {
    const PP = this._parent;
    const V = this._right;
    const R = V._left;
    if (PP._parent === this) PP._parent = V;else if (PP._left === this) PP._left = V;else PP._right = V;
    V._parent = PP;
    V._left = this;
    this._parent = V;
    this._right = R;
    if (R) R._parent = this;
    return V;
  }
  /**
   * @description Rotate right.
   * @returns TreeNode about moved to original position after rotation.
   */
  _rotateRight() {
    const PP = this._parent;
    const F = this._left;
    const K = F._right;
    if (PP._parent === this) PP._parent = F;else if (PP._left === this) PP._left = F;else PP._right = F;
    F._parent = PP;
    F._right = this;
    this._parent = F;
    this._left = K;
    if (K) K._parent = this;
    return F;
  }
}
class TreeNodeEnableIndex extends TreeNode {
  constructor() {
    super(...arguments);
    this._subTreeSize = 1;
  }
  /**
   * @description Rotate left and do recount.
   * @returns TreeNode about moved to original position after rotation.
   */
  _rotateLeft() {
    const parent = super._rotateLeft();
    this._recount();
    parent._recount();
    return parent;
  }
  /**
   * @description Rotate right and do recount.
   * @returns TreeNode about moved to original position after rotation.
   */
  _rotateRight() {
    const parent = super._rotateRight();
    this._recount();
    parent._recount();
    return parent;
  }
  _recount() {
    this._subTreeSize = 1;
    if (this._left) {
      this._subTreeSize += this._left._subTreeSize;
    }
    if (this._right) {
      this._subTreeSize += this._right._subTreeSize;
    }
  }
}

class ContainerIterator {
  /**
   * @internal
   */
  constructor(iteratorType = 0 /* IteratorType.NORMAL */) {
    this.iteratorType = iteratorType;
  }
  /**
   * @param iter - The other iterator you want to compare.
   * @returns Whether this equals to obj.
   * @example
   * container.find(1).equals(container.end());
   */
  equals(iter) {
    return this._node === iter._node;
  }
}
class Base {
  constructor() {
    /**
     * @description Container's size.
     * @internal
     */
    this._length = 0;
  }
  /**
   * @returns The size of the container.
   * @example
   * const container = new Vector([1, 2]);
   * console.log(container.length); // 2
   */
  get length() {
    return this._length;
  }
  /**
   * @returns The size of the container.
   * @example
   * const container = new Vector([1, 2]);
   * console.log(container.size()); // 2
   */
  size() {
    return this._length;
  }
  /**
   * @returns Whether the container is empty.
   * @example
   * container.clear();
   * console.log(container.empty());  // true
   */
  empty() {
    return this._length === 0;
  }
}
class Container extends Base {}

/**
 * @description Throw an iterator access error.
 * @internal
 */
function throwIteratorAccessError() {
  throw new RangeError('Iterator access denied!');
}

class TreeContainer extends Container {
  /**
   * @internal
   */
  constructor(cmp = function (x, y) {
    if (x < y) return -1;
    if (x > y) return 1;
    return 0;
  }, enableIndex = false) {
    super();
    /**
     * @internal
     */
    this._root = undefined;
    this._cmp = cmp;
    this.enableIndex = enableIndex;
    this._TreeNodeClass = enableIndex ? TreeNodeEnableIndex : TreeNode;
    this._header = new this._TreeNodeClass();
  }
  /**
   * @internal
   */
  _lowerBound(curNode, key) {
    let resNode = this._header;
    while (curNode) {
      const cmpResult = this._cmp(curNode._key, key);
      if (cmpResult < 0) {
        curNode = curNode._right;
      } else if (cmpResult > 0) {
        resNode = curNode;
        curNode = curNode._left;
      } else return curNode;
    }
    return resNode;
  }
  /**
   * @internal
   */
  _upperBound(curNode, key) {
    let resNode = this._header;
    while (curNode) {
      const cmpResult = this._cmp(curNode._key, key);
      if (cmpResult <= 0) {
        curNode = curNode._right;
      } else {
        resNode = curNode;
        curNode = curNode._left;
      }
    }
    return resNode;
  }
  /**
   * @internal
   */
  _reverseLowerBound(curNode, key) {
    let resNode = this._header;
    while (curNode) {
      const cmpResult = this._cmp(curNode._key, key);
      if (cmpResult < 0) {
        resNode = curNode;
        curNode = curNode._right;
      } else if (cmpResult > 0) {
        curNode = curNode._left;
      } else return curNode;
    }
    return resNode;
  }
  /**
   * @internal
   */
  _reverseUpperBound(curNode, key) {
    let resNode = this._header;
    while (curNode) {
      const cmpResult = this._cmp(curNode._key, key);
      if (cmpResult < 0) {
        resNode = curNode;
        curNode = curNode._right;
      } else {
        curNode = curNode._left;
      }
    }
    return resNode;
  }
  /**
   * @internal
   */
  _eraseNodeSelfBalance(curNode) {
    while (true) {
      const parentNode = curNode._parent;
      if (parentNode === this._header) return;
      if (curNode._color === 1 /* TreeNodeColor.RED */) {
        curNode._color = 0 /* TreeNodeColor.BLACK */;
        return;
      }
      if (curNode === parentNode._left) {
        const brother = parentNode._right;
        if (brother._color === 1 /* TreeNodeColor.RED */) {
          brother._color = 0 /* TreeNodeColor.BLACK */;
          parentNode._color = 1 /* TreeNodeColor.RED */;
          if (parentNode === this._root) {
            this._root = parentNode._rotateLeft();
          } else parentNode._rotateLeft();
        } else {
          if (brother._right && brother._right._color === 1 /* TreeNodeColor.RED */) {
            brother._color = parentNode._color;
            parentNode._color = 0 /* TreeNodeColor.BLACK */;
            brother._right._color = 0 /* TreeNodeColor.BLACK */;
            if (parentNode === this._root) {
              this._root = parentNode._rotateLeft();
            } else parentNode._rotateLeft();
            return;
          } else if (brother._left && brother._left._color === 1 /* TreeNodeColor.RED */) {
            brother._color = 1 /* TreeNodeColor.RED */;
            brother._left._color = 0 /* TreeNodeColor.BLACK */;
            brother._rotateRight();
          } else {
            brother._color = 1 /* TreeNodeColor.RED */;
            curNode = parentNode;
          }
        }
      } else {
        const brother = parentNode._left;
        if (brother._color === 1 /* TreeNodeColor.RED */) {
          brother._color = 0 /* TreeNodeColor.BLACK */;
          parentNode._color = 1 /* TreeNodeColor.RED */;
          if (parentNode === this._root) {
            this._root = parentNode._rotateRight();
          } else parentNode._rotateRight();
        } else {
          if (brother._left && brother._left._color === 1 /* TreeNodeColor.RED */) {
            brother._color = parentNode._color;
            parentNode._color = 0 /* TreeNodeColor.BLACK */;
            brother._left._color = 0 /* TreeNodeColor.BLACK */;
            if (parentNode === this._root) {
              this._root = parentNode._rotateRight();
            } else parentNode._rotateRight();
            return;
          } else if (brother._right && brother._right._color === 1 /* TreeNodeColor.RED */) {
            brother._color = 1 /* TreeNodeColor.RED */;
            brother._right._color = 0 /* TreeNodeColor.BLACK */;
            brother._rotateLeft();
          } else {
            brother._color = 1 /* TreeNodeColor.RED */;
            curNode = parentNode;
          }
        }
      }
    }
  }
  /**
   * @internal
   */
  _eraseNode(curNode) {
    if (this._length === 1) {
      this.clear();
      return;
    }
    let swapNode = curNode;
    while (swapNode._left || swapNode._right) {
      if (swapNode._right) {
        swapNode = swapNode._right;
        while (swapNode._left) swapNode = swapNode._left;
      } else {
        swapNode = swapNode._left;
      }
      const key = curNode._key;
      curNode._key = swapNode._key;
      swapNode._key = key;
      const value = curNode._value;
      curNode._value = swapNode._value;
      swapNode._value = value;
      curNode = swapNode;
    }
    if (this._header._left === swapNode) {
      this._header._left = swapNode._parent;
    } else if (this._header._right === swapNode) {
      this._header._right = swapNode._parent;
    }
    this._eraseNodeSelfBalance(swapNode);
    let _parent = swapNode._parent;
    if (swapNode === _parent._left) {
      _parent._left = undefined;
    } else _parent._right = undefined;
    this._length -= 1;
    this._root._color = 0 /* TreeNodeColor.BLACK */;
    if (this.enableIndex) {
      while (_parent !== this._header) {
        _parent._subTreeSize -= 1;
        _parent = _parent._parent;
      }
    }
  }
  /**
   * @internal
   */
  _inOrderTraversal(param) {
    const pos = typeof param === 'number' ? param : undefined;
    const callback = typeof param === 'function' ? param : undefined;
    const nodeList = typeof param === 'undefined' ? [] : undefined;
    let index = 0;
    let curNode = this._root;
    const stack = [];
    while (stack.length || curNode) {
      if (curNode) {
        stack.push(curNode);
        curNode = curNode._left;
      } else {
        curNode = stack.pop();
        if (index === pos) return curNode;
        nodeList && nodeList.push(curNode);
        callback && callback(curNode, index, this);
        index += 1;
        curNode = curNode._right;
      }
    }
    return nodeList;
  }
  /**
   * @internal
   */
  _insertNodeSelfBalance(curNode) {
    while (true) {
      const parentNode = curNode._parent;
      if (parentNode._color === 0 /* TreeNodeColor.BLACK */) return;
      const grandParent = parentNode._parent;
      if (parentNode === grandParent._left) {
        const uncle = grandParent._right;
        if (uncle && uncle._color === 1 /* TreeNodeColor.RED */) {
          uncle._color = parentNode._color = 0 /* TreeNodeColor.BLACK */;
          if (grandParent === this._root) return;
          grandParent._color = 1 /* TreeNodeColor.RED */;
          curNode = grandParent;
          continue;
        } else if (curNode === parentNode._right) {
          curNode._color = 0 /* TreeNodeColor.BLACK */;
          if (curNode._left) {
            curNode._left._parent = parentNode;
          }
          if (curNode._right) {
            curNode._right._parent = grandParent;
          }
          parentNode._right = curNode._left;
          grandParent._left = curNode._right;
          curNode._left = parentNode;
          curNode._right = grandParent;
          if (grandParent === this._root) {
            this._root = curNode;
            this._header._parent = curNode;
          } else {
            const GP = grandParent._parent;
            if (GP._left === grandParent) {
              GP._left = curNode;
            } else GP._right = curNode;
          }
          curNode._parent = grandParent._parent;
          parentNode._parent = curNode;
          grandParent._parent = curNode;
          grandParent._color = 1 /* TreeNodeColor.RED */;
        } else {
          parentNode._color = 0 /* TreeNodeColor.BLACK */;
          if (grandParent === this._root) {
            this._root = grandParent._rotateRight();
          } else grandParent._rotateRight();
          grandParent._color = 1 /* TreeNodeColor.RED */;
          return;
        }
      } else {
        const uncle = grandParent._left;
        if (uncle && uncle._color === 1 /* TreeNodeColor.RED */) {
          uncle._color = parentNode._color = 0 /* TreeNodeColor.BLACK */;
          if (grandParent === this._root) return;
          grandParent._color = 1 /* TreeNodeColor.RED */;
          curNode = grandParent;
          continue;
        } else if (curNode === parentNode._left) {
          curNode._color = 0 /* TreeNodeColor.BLACK */;
          if (curNode._left) {
            curNode._left._parent = grandParent;
          }
          if (curNode._right) {
            curNode._right._parent = parentNode;
          }
          grandParent._right = curNode._left;
          parentNode._left = curNode._right;
          curNode._left = grandParent;
          curNode._right = parentNode;
          if (grandParent === this._root) {
            this._root = curNode;
            this._header._parent = curNode;
          } else {
            const GP = grandParent._parent;
            if (GP._left === grandParent) {
              GP._left = curNode;
            } else GP._right = curNode;
          }
          curNode._parent = grandParent._parent;
          parentNode._parent = curNode;
          grandParent._parent = curNode;
          grandParent._color = 1 /* TreeNodeColor.RED */;
        } else {
          parentNode._color = 0 /* TreeNodeColor.BLACK */;
          if (grandParent === this._root) {
            this._root = grandParent._rotateLeft();
          } else grandParent._rotateLeft();
          grandParent._color = 1 /* TreeNodeColor.RED */;
          return;
        }
      }
      if (this.enableIndex) {
        parentNode._recount();
        grandParent._recount();
        curNode._recount();
      }
      return;
    }
  }
  /**
   * @internal
   */
  _set(key, value, hint) {
    if (this._root === undefined) {
      this._length += 1;
      this._root = new this._TreeNodeClass(key, value, 0 /* TreeNodeColor.BLACK */);
      this._root._parent = this._header;
      this._header._parent = this._header._left = this._header._right = this._root;
      return this._length;
    }
    let curNode;
    const minNode = this._header._left;
    const compareToMin = this._cmp(minNode._key, key);
    if (compareToMin === 0) {
      minNode._value = value;
      return this._length;
    } else if (compareToMin > 0) {
      minNode._left = new this._TreeNodeClass(key, value);
      minNode._left._parent = minNode;
      curNode = minNode._left;
      this._header._left = curNode;
    } else {
      const maxNode = this._header._right;
      const compareToMax = this._cmp(maxNode._key, key);
      if (compareToMax === 0) {
        maxNode._value = value;
        return this._length;
      } else if (compareToMax < 0) {
        maxNode._right = new this._TreeNodeClass(key, value);
        maxNode._right._parent = maxNode;
        curNode = maxNode._right;
        this._header._right = curNode;
      } else {
        if (hint !== undefined) {
          const iterNode = hint._node;
          if (iterNode !== this._header) {
            const iterCmpRes = this._cmp(iterNode._key, key);
            if (iterCmpRes === 0) {
              iterNode._value = value;
              return this._length;
            } else /* istanbul ignore else */if (iterCmpRes > 0) {
                const preNode = iterNode._pre();
                const preCmpRes = this._cmp(preNode._key, key);
                if (preCmpRes === 0) {
                  preNode._value = value;
                  return this._length;
                } else if (preCmpRes < 0) {
                  curNode = new this._TreeNodeClass(key, value);
                  if (preNode._right === undefined) {
                    preNode._right = curNode;
                    curNode._parent = preNode;
                  } else {
                    iterNode._left = curNode;
                    curNode._parent = iterNode;
                  }
                }
              }
          }
        }
        if (curNode === undefined) {
          curNode = this._root;
          while (true) {
            const cmpResult = this._cmp(curNode._key, key);
            if (cmpResult > 0) {
              if (curNode._left === undefined) {
                curNode._left = new this._TreeNodeClass(key, value);
                curNode._left._parent = curNode;
                curNode = curNode._left;
                break;
              }
              curNode = curNode._left;
            } else if (cmpResult < 0) {
              if (curNode._right === undefined) {
                curNode._right = new this._TreeNodeClass(key, value);
                curNode._right._parent = curNode;
                curNode = curNode._right;
                break;
              }
              curNode = curNode._right;
            } else {
              curNode._value = value;
              return this._length;
            }
          }
        }
      }
    }
    if (this.enableIndex) {
      let parent = curNode._parent;
      while (parent !== this._header) {
        parent._subTreeSize += 1;
        parent = parent._parent;
      }
    }
    this._insertNodeSelfBalance(curNode);
    this._length += 1;
    return this._length;
  }
  /**
   * @internal
   */
  _getTreeNodeByKey(curNode, key) {
    while (curNode) {
      const cmpResult = this._cmp(curNode._key, key);
      if (cmpResult < 0) {
        curNode = curNode._right;
      } else if (cmpResult > 0) {
        curNode = curNode._left;
      } else return curNode;
    }
    return curNode || this._header;
  }
  clear() {
    this._length = 0;
    this._root = undefined;
    this._header._parent = undefined;
    this._header._left = this._header._right = undefined;
  }
  /**
   * @description Update node's key by iterator.
   * @param iter - The iterator you want to change.
   * @param key - The key you want to update.
   * @returns Whether the modification is successful.
   * @example
   * const st = new orderedSet([1, 2, 5]);
   * const iter = st.find(2);
   * st.updateKeyByIterator(iter, 3); // then st will become [1, 3, 5]
   */
  updateKeyByIterator(iter, key) {
    const node = iter._node;
    if (node === this._header) {
      throwIteratorAccessError();
    }
    if (this._length === 1) {
      node._key = key;
      return true;
    }
    const nextKey = node._next()._key;
    if (node === this._header._left) {
      if (this._cmp(nextKey, key) > 0) {
        node._key = key;
        return true;
      }
      return false;
    }
    const preKey = node._pre()._key;
    if (node === this._header._right) {
      if (this._cmp(preKey, key) < 0) {
        node._key = key;
        return true;
      }
      return false;
    }
    if (this._cmp(preKey, key) >= 0 || this._cmp(nextKey, key) <= 0) return false;
    node._key = key;
    return true;
  }
  eraseElementByPos(pos) {
    if (pos < 0 || pos > this._length - 1) {
      throw new RangeError();
    }
    const node = this._inOrderTraversal(pos);
    this._eraseNode(node);
    return this._length;
  }
  /**
   * @description Remove the element of the specified key.
   * @param key - The key you want to remove.
   * @returns Whether erase successfully.
   */
  eraseElementByKey(key) {
    if (this._length === 0) return false;
    const curNode = this._getTreeNodeByKey(this._root, key);
    if (curNode === this._header) return false;
    this._eraseNode(curNode);
    return true;
  }
  eraseElementByIterator(iter) {
    const node = iter._node;
    if (node === this._header) {
      throwIteratorAccessError();
    }
    const hasNoRight = node._right === undefined;
    const isNormal = iter.iteratorType === 0 /* IteratorType.NORMAL */;
    // For the normal iterator, the `next` node will be swapped to `this` node when has right.
    if (isNormal) {
      // So we should move it to next when it's right is null.
      if (hasNoRight) iter.next();
    } else {
      // For the reverse iterator, only when it doesn't have right and has left the `next` node will be swapped.
      // So when it has right, or it is a leaf node we should move it to `next`.
      if (!hasNoRight || node._left === undefined) iter.next();
    }
    this._eraseNode(node);
    return iter;
  }
  /**
   * @description Get the height of the tree.
   * @returns Number about the height of the RB-tree.
   */
  getHeight() {
    if (this._length === 0) return 0;
    function traversal(curNode) {
      if (!curNode) return 0;
      return Math.max(traversal(curNode._left), traversal(curNode._right)) + 1;
    }
    return traversal(this._root);
  }
}

class TreeIterator extends ContainerIterator {
  /**
   * @internal
   */
  constructor(node, header, iteratorType) {
    super(iteratorType);
    this._node = node;
    this._header = header;
    if (this.iteratorType === 0 /* IteratorType.NORMAL */) {
      this.pre = function () {
        if (this._node === this._header._left) {
          throwIteratorAccessError();
        }
        this._node = this._node._pre();
        return this;
      };
      this.next = function () {
        if (this._node === this._header) {
          throwIteratorAccessError();
        }
        this._node = this._node._next();
        return this;
      };
    } else {
      this.pre = function () {
        if (this._node === this._header._right) {
          throwIteratorAccessError();
        }
        this._node = this._node._next();
        return this;
      };
      this.next = function () {
        if (this._node === this._header) {
          throwIteratorAccessError();
        }
        this._node = this._node._pre();
        return this;
      };
    }
  }
  /**
   * @description Get the sequential index of the iterator in the tree container.<br/>
   *              <strong>Note:</strong>
   *              This function only takes effect when the specified tree container `enableIndex = true`.
   * @returns The index subscript of the node in the tree.
   * @example
   * const st = new OrderedSet([1, 2, 3], true);
   * console.log(st.begin().next().index);  // 1
   */
  get index() {
    let _node = this._node;
    const root = this._header._parent;
    if (_node === this._header) {
      if (root) {
        return root._subTreeSize - 1;
      }
      return 0;
    }
    let index = 0;
    if (_node._left) {
      index += _node._left._subTreeSize;
    }
    while (_node !== root) {
      const _parent = _node._parent;
      if (_node === _parent._right) {
        index += 1;
        if (_parent._left) {
          index += _parent._left._subTreeSize;
        }
      }
      _node = _parent;
    }
    return index;
  }
  isAccessible() {
    return this._node !== this._header;
  }
}

class OrderedSetIterator extends TreeIterator {
  constructor(node, header, container, iteratorType) {
    super(node, header, iteratorType);
    this.container = container;
  }
  get pointer() {
    if (this._node === this._header) {
      throwIteratorAccessError();
    }
    return this._node._key;
  }
  copy() {
    return new OrderedSetIterator(this._node, this._header, this.container, this.iteratorType);
  }
}
class OrderedSet extends TreeContainer {
  /**
   * @param container - The initialization container.
   * @param cmp - The compare function.
   * @param enableIndex - Whether to enable iterator indexing function.
   * @example
   * new OrderedSet();
   * new OrderedSet([0, 1, 2]);
   * new OrderedSet([0, 1, 2], (x, y) => x - y);
   * new OrderedSet([0, 1, 2], (x, y) => x - y, true);
   */
  constructor(container = [], cmp, enableIndex) {
    super(cmp, enableIndex);
    const self = this;
    container.forEach(function (el) {
      self.insert(el);
    });
  }
  begin() {
    return new OrderedSetIterator(this._header._left || this._header, this._header, this);
  }
  end() {
    return new OrderedSetIterator(this._header, this._header, this);
  }
  rBegin() {
    return new OrderedSetIterator(this._header._right || this._header, this._header, this, 1 /* IteratorType.REVERSE */);
  }

  rEnd() {
    return new OrderedSetIterator(this._header, this._header, this, 1 /* IteratorType.REVERSE */);
  }

  front() {
    return this._header._left ? this._header._left._key : undefined;
  }
  back() {
    return this._header._right ? this._header._right._key : undefined;
  }
  lowerBound(key) {
    const resNode = this._lowerBound(this._root, key);
    return new OrderedSetIterator(resNode, this._header, this);
  }
  upperBound(key) {
    const resNode = this._upperBound(this._root, key);
    return new OrderedSetIterator(resNode, this._header, this);
  }
  reverseLowerBound(key) {
    const resNode = this._reverseLowerBound(this._root, key);
    return new OrderedSetIterator(resNode, this._header, this);
  }
  reverseUpperBound(key) {
    const resNode = this._reverseUpperBound(this._root, key);
    return new OrderedSetIterator(resNode, this._header, this);
  }
  forEach(callback) {
    this._inOrderTraversal(function (node, index, set) {
      callback(node._key, index, set);
    });
  }
  /**
   * @description Insert element to set.
   * @param key - The key want to insert.
   * @param hint - You can give an iterator hint to improve insertion efficiency.
   * @return The size of container after setting.
   * @example
   * const st = new OrderedSet([2, 4, 5]);
   * const iter = st.begin();
   * st.insert(1);
   * st.insert(3, iter);  // give a hint will be faster.
   */
  insert(key, hint) {
    return this._set(key, undefined, hint);
  }
  getElementByPos(pos) {
    if (pos < 0 || pos > this._length - 1) {
      throw new RangeError();
    }
    const node = this._inOrderTraversal(pos);
    return node._key;
  }
  find(element) {
    const resNode = this._getTreeNodeByKey(this._root, element);
    return new OrderedSetIterator(resNode, this._header, this);
  }
  union(other) {
    const self = this;
    other.forEach(function (el) {
      self.insert(el);
    });
    return this._length;
  }
  *[Symbol.iterator]() {
    const length = this._length;
    const nodeList = this._inOrderTraversal();
    for (let i = 0; i < length; ++i) {
      yield nodeList[i]._key;
    }
  }
}

exports.OrderedSet = OrderedSet;
//# sourceMappingURL=index.js.map
